import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Book, Search, ChevronRight, Edit, Plus, Clock, User,
  BookOpen, HelpCircle, Settings, RefreshCw, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  'Getting Started': BookOpen,
  'Workflows': RefreshCw,
  'Operations': Settings,
  'Help': HelpCircle,
  'Updates': Clock
};

const Library = () => {
  const { isOwner } = useAuth();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  
  // Admin modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: 'Getting Started',
    content: '',
    order: 0,
    is_published: true
  });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [articlesRes, categoriesRes, updatesRes] = await Promise.all([
        axios.get(`${API}/library`),
        axios.get(`${API}/library/categories`),
        axios.get(`${API}/library/updates`)
      ]);
      setArticles(articlesRes.data);
      setCategories(categoriesRes.data);
      setUpdates(updatesRes.data);
    } catch (error) {
      console.error('Failed to fetch library:', error);
      toast.error('Failed to load library');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSeed = async () => {
    try {
      const response = await axios.post(`${API}/library/seed`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed library');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      category: 'Getting Started',
      content: '',
      order: 0,
      is_published: true
    });
    setEditingArticle(null);
  };

  const handleOpenEdit = (article) => {
    setFormData({
      title: article.title,
      slug: article.slug,
      category: article.category,
      content: article.content,
      order: article.order,
      is_published: article.is_published
    });
    setEditingArticle(article);
    setShowEditModal(true);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowEditModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.slug.trim()) {
      toast.error('Title and slug are required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingArticle) {
        await axios.put(`${API}/library/${editingArticle.slug}`, formData);
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/library`, formData);
        toast.success('Article created');
      }
      setShowEditModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const filteredArticles = articles.filter(a => {
    const matchesSearch = a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || a.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Group articles by category
  const articlesByCategory = {};
  filteredArticles.forEach(article => {
    if (!articlesByCategory[article.category]) {
      articlesByCategory[article.category] = [];
    }
    articlesByCategory[article.category].push(article);
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-JM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Article detail view
  if (selectedArticle) {
    return (
      <Layout>
        <div className="page-container">
          <button
            onClick={() => setSelectedArticle(null)}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-4"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back to Library
          </button>
          
          <div className="card p-6 lg:p-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <span className="text-sm text-blue-600 font-medium">{selectedArticle.category}</span>
                <h1 className="text-2xl font-bold text-slate-900 mt-1">{selectedArticle.title}</h1>
                <p className="text-sm text-slate-500 mt-2">
                  Last updated: {formatDate(selectedArticle.updated_at || selectedArticle.created_at)}
                </p>
              </div>
              {isOwner && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleOpenEdit(selectedArticle)}
                  className="border-2"
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              )}
            </div>
            
            <div className="prose prose-slate max-w-none">
              {/* Render markdown content as HTML-like structure */}
              {selectedArticle.content.split('\n').map((line, i) => {
                if (line.startsWith('# ')) {
                  return <h1 key={i} className="text-2xl font-bold text-slate-900 mt-6 mb-4">{line.substring(2)}</h1>;
                } else if (line.startsWith('## ')) {
                  return <h2 key={i} className="text-xl font-semibold text-slate-800 mt-5 mb-3">{line.substring(3)}</h2>;
                } else if (line.startsWith('### ')) {
                  return <h3 key={i} className="text-lg font-medium text-slate-700 mt-4 mb-2">{line.substring(4)}</h3>;
                } else if (line.startsWith('- ')) {
                  return <li key={i} className="text-slate-600 ml-4">{line.substring(2)}</li>;
                } else if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={i} className="font-semibold text-slate-800 my-2">{line.slice(2, -2)}</p>;
                } else if (line.startsWith('**Q:')) {
                  return <p key={i} className="font-semibold text-slate-800 mt-4">{line.replace(/\*\*/g, '')}</p>;
                } else if (line.startsWith('A:')) {
                  return <p key={i} className="text-slate-600 mb-4">{line}</p>;
                } else if (line.trim() === '') {
                  return <br key={i} />;
                } else {
                  return <p key={i} className="text-slate-600 my-2">{line}</p>;
                }
              })}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Book className="w-6 h-6 text-blue-600" />
              Library
            </h1>
            <p className="text-slate-500">Knowledge base and documentation</p>
          </div>
          {isOwner && (
            <div className="flex gap-2">
              {articles.length === 0 && (
                <Button onClick={handleSeed} variant="outline" className="border-2">
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Seed Content
                </Button>
              )}
              <Button onClick={handleOpenCreate} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-1" />
                Add Article
              </Button>
            </div>
          )}
        </div>

        {/* Search and Filter */}
        <div className="card p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.category} value={cat.category}>
                    {cat.category} ({cat.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="card p-8 text-center">
            <div className="text-slate-500">Loading library...</div>
          </div>
        ) : articles.length === 0 ? (
          <div className="card p-8 text-center">
            <Book className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">Library is empty</p>
            <p className="text-sm text-slate-500 mb-4">
              {isOwner ? 'Click "Seed Content" to add initial articles' : 'No articles available yet'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {Object.entries(articlesByCategory).map(([category, catArticles]) => {
                const CategoryIcon = CATEGORY_ICONS[category] || FileText;
                return (
                  <div key={category} className="card">
                    <div className="p-4 border-b border-slate-100 flex items-center gap-2">
                      <CategoryIcon className="w-5 h-5 text-blue-600" />
                      <h2 className="font-semibold text-slate-900">{category}</h2>
                      <span className="text-sm text-slate-500">({catArticles.length})</span>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {catArticles.map((article) => (
                        <div
                          key={article.id}
                          className="p-4 hover:bg-slate-50 cursor-pointer flex items-center justify-between"
                          onClick={() => setSelectedArticle(article)}
                        >
                          <div>
                            <p className="font-medium text-slate-900">{article.title}</p>
                            {!article.is_published && (
                              <span className="text-xs text-amber-600">(Draft)</span>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Sidebar - Updates */}
            <div className="space-y-6">
              <div className="card">
                <div className="p-4 border-b border-slate-100 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-slate-900">Recent Updates</h3>
                </div>
                {updates.length > 0 ? (
                  <div className="divide-y divide-slate-100">
                    {updates.slice(0, 5).map((update) => (
                      <div key={update.id} className="p-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-mono text-blue-600">v{update.version}</span>
                          <span className="text-xs text-slate-500">{formatDate(update.created_at)}</span>
                        </div>
                        <p className="text-sm text-slate-700 font-medium">{update.summary}</p>
                        <ul className="mt-2 space-y-1">
                          {update.changes.slice(0, 3).map((change, i) => (
                            <li key={i} className="text-xs text-slate-500 flex items-start gap-1">
                              <span className="text-blue-500">•</span>
                              {change}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    No updates yet
                  </div>
                )}
              </div>

              {/* Quick Links */}
              <div className="card p-4">
                <h3 className="font-semibold text-slate-900 mb-3">Quick Links</h3>
                <div className="space-y-2">
                  {articles.filter(a => ['introduction', 'roles', 'faq'].includes(a.slug)).map((article) => (
                    <button
                      key={article.id}
                      onClick={() => setSelectedArticle(article)}
                      className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-100 text-sm text-slate-700 flex items-center gap-2"
                    >
                      <ChevronRight className="w-3 h-3 text-slate-400" />
                      {article.title}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="sm:max-w-2xl border-2 border-slate-200 max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-slate-900 flex items-center gap-2">
                <Edit className="w-5 h-5 text-blue-600" />
                {editingArticle ? 'Edit Article' : 'Create Article'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Article title"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="slug">Slug *</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    placeholder="url-friendly-slug"
                    required
                    disabled={!!editingArticle}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Getting Started">Getting Started</SelectItem>
                      <SelectItem value="Workflows">Workflows</SelectItem>
                      <SelectItem value="Operations">Operations</SelectItem>
                      <SelectItem value="Help">Help</SelectItem>
                      <SelectItem value="Updates">Updates</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="order">Display Order</Label>
                  <Input
                    id="order"
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="content">Content (Markdown)</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="# Heading&#10;&#10;Your content here..."
                  className="min-h-[300px] font-mono text-sm"
                />
              </div>
              <div className="flex items-center justify-between py-2">
                <div>
                  <Label>Published</Label>
                  <p className="text-xs text-slate-500">Unpublished articles are only visible to Owner Admin</p>
                </div>
                <Switch
                  checked={formData.is_published}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 border-2"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingArticle ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Library;
